import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { EXPERIENCES, EXCURSION_IMAGE } from "@/lib/data";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageHero from "@/components/PageHero";
import PageStickyNav from "@/components/PageStickyNav";
import XalucaOriginalsBadge from "@/components/XalucaOriginalsBadge";
import XalucaOriginalsCarousel from "@/components/XalucaOriginalsCarousel";
import BespokeExperiencesSection from "@/components/BespokeExperiencesSection";

const ExperiencesPage = () => {
  const { t, lang } = useLanguage();

  // Flagship experiences keep the editorial 2-column grid. "Originals" are
  // shorter activity-style add-ons surfaced in a horizontal carousel below.
  const { flagship, originals } = useMemo(() => {
    const f = [];
    const o = [];
    EXPERIENCES.forEach((ex) => (ex.secondary ? o.push(ex) : f.push(ex)));
    return { flagship: f, originals: o };
  }, []);

  const discoverLabel =
    lang === "fr"
      ? "Découvrir l'expérience"
      : lang === "es"
      ? "Descubrir la experiencia"
      : "Discover this experience";

  return (
    <div data-testid="page-experiences">
      <section data-testid="experiences-hero">
        <PageHero
          images={[
            EXCURSION_IMAGE,
            "https://customer-assets.emergentagent.com/job_xaluca-luxury/artifacts/htd6p1fu_Kasbah%20Hotel%20Xaluca%20-%20Ae%CC%81reas%20-%20HamidBabni-4.jpg",
            "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=2400&q=80",
          ]}
          alt="Experiences · Grup Xaluca"
          scope="page.experiences"
          overline={t("sections.experiences_overline")}
          title={t("sections.experiences_title")}
          sub={t("sections.experiences_sub")}
        />
      </section>

      <PageStickyNav
        items={[
          { id: "experiences-flagship-section", label: { en: "Flagship", fr: "Phares", es: "Estelares" } },
          { id: "experiences-originals-section", label: { en: "Originals", fr: "Originales", es: "Originales" } },
        ]}
      />

      {/* Flagship editorial grid */}
      <section id="experiences-flagship-section" className="py-20 md:py-28 scroll-mt-32" data-testid="experiences-flagship-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-x-8 gap-y-16">
          {flagship.map((ex) => (
            <article
              key={ex.key}
              className="group"
              data-testid={`experience-card-${ex.key}`}
              data-cms-scope={`experiences.flagship.${ex.key}`}
            >
              <Link
                to={`/experiences/${ex.key}`}
                className="block relative overflow-hidden aspect-[4/3]"
                data-testid={`experience-link-${ex.key}`}
              >
                <img
                  src={ex.image}
                  alt={ex.title.en}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async"
                  data-cms-role="card.image"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <XalucaOriginalsBadge
                  variant="dark"
                  className="absolute top-4 left-4"
                  testid={`experience-card-${ex.key}-badge`}
                />
                {/* X monogram border treatment — consistent with the rest of the site */}
                <BrandMonogramEdge
                  size="lg"
                  opacity={0.45}
                  testIdPrefix={`experience-card-${ex.key}-brand`}
                />
              </Link>
              <h3 className="font-serif-x text-3xl md:text-4xl mt-6 tracking-tight">
                <Link to={`/experiences/${ex.key}`} className="hover:text-[#C16542] transition-colors">
                  {ex.title[lang] || ex.title.en}
                </Link>
              </h3>
              <p className="mt-4 text-lg text-[#5C5248] leading-relaxed max-w-xl">
                {ex.desc[lang] || ex.desc.en}
              </p>
              <Link
                to={`/experiences/${ex.key}`}
                className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#2C2621] hover:text-[#C16542] border-b border-[#2C2621]/30 hover:border-[#C16542] pb-1 transition-colors"
                data-testid={`experience-discover-${ex.key}`}
              >
                {discoverLabel}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            </article>
          ))}
        </div>
      </section>

      {/* Secondary Xaluca Originals — horizontal carousel */}
      {originals.length > 0 && (
        <section
          id="experiences-originals-section"
          className="py-20 md:py-28 bg-[#F2EBE1]/40 border-y border-[#2C2621]/10 scroll-mt-32"
          data-testid="experiences-originals-section"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-12 gap-8 mb-10 md:mb-14">
              <div className="md:col-span-7">
                <p className="overline">
                  {lang === "fr"
                    ? "Plus d'expériences"
                    : lang === "es"
                    ? "Más experiencias"
                    : "More to explore"}
                </p>
                <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#2C2621]">
                  {lang === "fr"
                    ? "Xaluca Originals — l'agenda du concierge"
                    : lang === "es"
                    ? "Xaluca Originals — la agenda del conserje"
                    : "Xaluca Originals — the concierge's little black book"}
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 flex md:items-end">
                <p className="text-base md:text-lg leading-relaxed text-[#5C5248]">
                  {lang === "fr"
                    ? "De courtes excursions, demi-journées et soirées que nous composons à la demande — un complément parfait à votre séjour."
                    : lang === "es"
                    ? "Excursiones cortas, medias jornadas y veladas que componemos a medida — el complemento perfecto a su estancia."
                    : "Short trips, half-days and evenings we build on request — the perfect complement to a longer stay."}
                </p>
              </div>
            </div>
            <XalucaOriginalsCarousel items={originals} lang={lang} />
          </div>
        </section>
      )}

      {/* BESPOKE EXPERIENCES — immersive CTA section anchoring the page
          just above the footer. Invites visitors who couldn't find what
          they wanted to brief the concierge directly. */}
      <BespokeExperiencesSection />
    </div>
  );
};

export default ExperiencesPage;
